import { ColDef, ValueGetterParams, ValueSetterParams } from '@ag-grid-community/core';
import { DbSaveService } from '@data';
import { Guideline, GuidelineAccount, GuidelineOverride, GuidelineProgram, GuidelineSupplier } from '@models';
import { Entity } from 'breeze-client';

import { AgFns } from '../components/ag-grid/ag-fns';

type ListRow = GuidelineAccount | GuidelineSupplier | GuidelineProgram | GuidelineOverride;

/**
 * One list on a Guideline: its Accounts, Suppliers or Programs scope, or the rules it Overrides.
 * A scope has an "All" choice (allProp); Overrides has none.
 */
export interface GuidelineList {
  allLabel?: string;
  allProp?: 'isAllAccounts' | 'isAllSuppliers' | 'isAllPrograms';
  mapProp: 'guidelineAccounts' | 'guidelineSuppliers' | 'guidelinePrograms' | 'guidelineOverrides';
  keyProp: 'accountId' | 'supplierId' | 'programId' | 'overriddenGuidelineId';
  mapType: { new (): Entity };
  /** Every choice, by id and display name. A function, because Overrides lists the other rules. */
  choices: (g: Guideline) => { id: string; name: string }[];
}

export const ALL_ACCOUNTS = 'All Accounts';
export const ALL_SUPPLIERS = 'All Suppliers';
export const ALL_PROGRAMS = 'All Programs';

/**
 * Edits a Guideline's list rows (Accounts, Suppliers, Programs, Overrides) from a multi-select grid
 * cell. Shared by Utilities > Guidelines and the Guidelines tab of the Program form.
 *
 * The "All" flags are explicit: picking "All" clears the list, and an empty list without "All" is
 * refused at save (see problems), so removing the last row never widens a rule.
 */
export class GuidelineListEditor {
  constructor(private dbSaveService: DbSaveService) {}

  static accounts(choices: { id: string; name: string }[]): GuidelineList {
    return {
      allLabel: ALL_ACCOUNTS,
      allProp: 'isAllAccounts',
      mapProp: 'guidelineAccounts',
      keyProp: 'accountId',
      mapType: GuidelineAccount,
      choices: () => choices,
    };
  }

  static suppliers(choices: { id: string; name: string }[]): GuidelineList {
    return {
      allLabel: ALL_SUPPLIERS,
      allProp: 'isAllSuppliers',
      mapProp: 'guidelineSuppliers',
      keyProp: 'supplierId',
      mapType: GuidelineSupplier,
      choices: () => choices,
    };
  }

  static programs(choices: { id: string; name: string }[]): GuidelineList {
    return {
      allLabel: ALL_PROGRAMS,
      allProp: 'isAllPrograms',
      mapProp: 'guidelinePrograms',
      keyProp: 'programId',
      mapType: GuidelineProgram,
      choices: () => choices,
    };
  }

  /** The rules a rule replaces. Choices are the other rules, by Title or the start of the Instruction. */
  static overrides(rules: () => Guideline[]): GuidelineList {
    return {
      mapProp: 'guidelineOverrides',
      keyProp: 'overriddenGuidelineId',
      mapType: GuidelineOverride,
      choices: (g) =>
        rules()
          .filter((r) => r !== g && !r.entityAspect.entityState.isDetached())
          .map((r) => ({ id: r.id, name: GuidelineListEditor.ruleName(r) })),
    };
  }

  /** The name a rule shows in pickers and messages. */
  static ruleName(g: Guideline): string {
    if (g.title?.trim()) return g.title.trim();
    const text = (g.instruction ?? '').trim();
    return text ? (text.length > 60 ? text.slice(0, 57) + '...' : text) : '(new rule)';
  }

  colDef(headerName: string, list: GuidelineList, editable: (g: Guideline) => boolean, width = 220): ColDef {
    return {
      headerName,
      colId: list.mapProp,
      editable: (params) => editable(params.data),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      cellEditorParams: (params: { data: Guideline }) => ({
        values: [...(list.allLabel ? [list.allLabel] : []), ...list.choices(params.data).map((c) => c.name)],
        multiSelect: true,
        allowTyping: true,
        filterList: true,
        searchType: 'matchAny',
      }),
      valueGetter: (params: ValueGetterParams) => this.names(params.data, list),
      valueSetter: (params: ValueSetterParams) => this.set(params.data, list, params.newValue ?? []),
      valueFormatter: (params) => (params.value ?? []).join(', '),
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => this.names(params.data, list).join(', '),
      tooltipValueGetter: (params) => this.names(params.data, list).join(', '),
      ...AgFns.flexWidth(width),
    };
  }

  names(g: Guideline, list: GuidelineList): string[] {
    if (!g) return [];
    if (list.allProp && g[list.allProp]) return [list.allLabel!];
    const ids = new Set(this.rows(g, list).map((m) => this.keyOf(m, list)));
    return list.choices(g).filter((c) => ids.has(c.id)).map((c) => c.name);
  }

  set(g: Guideline, list: GuidelineList, names: string[]): boolean {
    const wasAll = list.allProp ? g[list.allProp] : false;
    const picked = names.filter((n) => n !== list.allLabel);
    // "All" wins when it is newly picked. When the rule was already "All" and names are added,
    // the admin is narrowing it to those names.
    const toAll = !!list.allLabel && names.includes(list.allLabel) && (!wasAll || picked.length === 0);
    const wantIds = new Set(toAll ? [] : list.choices(g).filter((c) => picked.includes(c.name)).map((c) => c.id));

    for (const m of [...this.rows(g, list)]) {
      const id = this.keyOf(m, list);
      if (wantIds.has(id)) {
        wantIds.delete(id);
      } else if (m.entityAspect.entityState.isAdded()) {
        m.entityAspect.setDetached();
      } else {
        m.entityAspect.setDeleted();
      }
    }
    for (const id of wantIds) {
      // A list row deleted earlier in this edit comes back instead of being created twice.
      const deleted = (this.dbSaveService.getChangesOfType(list.mapType) as unknown as ListRow[]).find(
        (m) => m.entityAspect.entityState.isDeleted() && m.guidelineId === g.id && this.keyOf(m, list) === id,
      );
      if (deleted) {
        deleted.entityAspect.rejectChanges();
      } else {
        this.dbSaveService.createEntity(list.mapType, { guidelineId: g.id, [list.keyProp]: id } as never);
      }
    }
    if (list.allProp) g[list.allProp] = toAll;
    return true;
  }

  /** Detaches an unsaved rule's list rows, before the rule itself is detached. */
  detachRows(g: Guideline) {
    [...g.guidelineAccounts, ...g.guidelineSuppliers, ...g.guidelinePrograms, ...g.guidelineOverrides].forEach((m) =>
      m.entityAspect.setDetached(),
    );
  }

  /** What stops a rule from saving. Empty = it may save. */
  problems(g: Guideline): string[] {
    const name = GuidelineListEditor.ruleName(g);
    const out: string[] = [];
    if (!(g.instruction ?? '').trim()) out.push(`${name} needs an Instruction - it is the rule Claude follows.`);
    if (!g.isAllAccounts && g.guidelineAccounts.length === 0) out.push(`${name}: choose ${ALL_ACCOUNTS} or at least one account.`);
    if (!g.isAllSuppliers && g.guidelineSuppliers.length === 0) out.push(`${name}: choose ${ALL_SUPPLIERS} or at least one supplier.`);
    if (!g.isAllPrograms && g.guidelinePrograms.length === 0) out.push(`${name}: choose ${ALL_PROGRAMS} or at least one program.`);
    if (g.guidelineOverrides.some((o) => o.overriddenGuidelineId === g.id)) out.push(`${name} cannot override itself.`);
    return out;
  }

  private rows(g: Guideline, list: GuidelineList): ListRow[] {
    return g[list.mapProp] as ListRow[];
  }

  private keyOf(m: ListRow, list: GuidelineList): string {
    return (m as unknown as Record<string, string>)[list.keyProp];
  }
}
